/**
 * The room for the system you are in.
 *
 * One room per system, joined by id, left when you jump. This is the first line
 * of the client that has ever spoken to the realtime server — `colyseus.js` has
 * been a dependency for a long time and imported by nothing.
 *
 * ── What this is not ────────────────────────────────────────────────────────
 *
 * Not a source of truth about your own ship. Movement is client-driven: you
 * simulate yourself at sixty frames a second and tell the room where you ended
 * up. What comes back is where *other people* are, and that is all it is for.
 * See `multiplayer-plan.md` for why, and for what that choice gives up.
 *
 * ── Joining is allowed to fail ──────────────────────────────────────────────
 *
 * A player with no realtime server is a player flying alone, not a player who
 * cannot fly. Everything here is best-effort and reports its state rather than
 * throwing: the game is entirely playable with this permanently disconnected,
 * and it was until now.
 */

import { Client, Room } from 'colyseus.js';
import type { SystemState } from '@interspacial/shared';

/**
 * Where the realtime server is.
 *
 * Same-origin in production behind nginx, which upgrades this path to the
 * websocket server; Vite proxies it in development. `wss` when the page is
 * served over TLS and `ws` when it is not, or the browser refuses the upgrade as
 * mixed content.
 */
function endpoint(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/interspacial/realtime`;
}

export type LinkState =
  /** Not trying. No token yet, or deliberately disconnected. */
  | 'idle'
  /** Joining, or re-joining after a system change. */
  | 'joining'
  /** In a room. Other ships will arrive as they move. */
  | 'joined'
  /** Tried and could not. The game carries on; you are simply alone. */
  | 'unavailable';

export interface RoomLink {
  /** Where we are with the server, for anything that wants to say so. */
  state: LinkState;
  /** The room itself, or null. Null is an ordinary state, not an error. */
  room: Room<SystemState> | null;
}

/**
 * Join the room for a system, leaving whatever we were in.
 *
 * `onChange` fires on every state transition, so a caller can render "alone" or
 * "connected" without polling.
 */
export function connectToSystem(options: {
  token: string;
  systemId: string;
  onChange: (link: RoomLink) => void;
}): () => void {
  const client = new Client(endpoint());

  let cancelled = false;
  let joined: Room<SystemState> | null = null;

  const report = (state: LinkState) => {
    if (!cancelled) options.onChange({ state, room: state === 'joined' ? joined : null });
  };

  report('joining');

  client
    .joinOrCreate<SystemState>('system', {
      systemId: options.systemId,
      token: options.token,
    })
    .then((room) => {
      // Jumped again, or unmounted, while the join was in flight. Leaving a room
      // nobody is going to read from is the whole cleanup.
      if (cancelled) {
        void room.leave();
        return;
      }

      joined = room;
      report('joined');

      room.onLeave(() => {
        joined = null;
        report('unavailable');
      });
    })
    .catch((error: unknown) => {
      // Expected whenever the realtime server is not running, which is most of
      // the time in development. Logged rather than surfaced, because there is
      // nothing for the player to do about it and the game works regardless.
      console.info('[room] flying alone:', (error as Error).message);
      report('unavailable');
    });

  return () => {
    cancelled = true;
    const room = joined;
    joined = null;
    if (room) void room.leave();
  };
}
